import React from "react";
import { Descriptions, Tag, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { PICKING_STATUS } from "../../../constants/PickingConstant";
import { usePickers } from "../../../data/hooks/usePickers";

const COLORS = {
  [PICKING_STATUS.QUEUED]: "blue",
  [PICKING_STATUS.PICKING]: "processing",
  [PICKING_STATUS.PICKED]: "green",
  [PICKING_STATUS.PARTIAL]: "orange",
  [PICKING_STATUS.ABORTED]: "red",
};

const time = (ms) => (ms ? dayjs(ms).format("D MMM HH:mm") : "-");

/** "Dick (BADGE-0042)" when the register knows the badge, else just the code. */
const usePickerLabel = () => {
  const { nameOf } = usePickers();
  return (code) => {
    if (!code) return null;
    const name = nameOf(code);
    return name ? `${name} (${code})` : code;
  };
};

/**
 * Where an order stands in the order picking (ported from the Picqer status tag). Reads
 * `order.picking`, which only the CMS writes (projection of the scanner's events); `orderStatus`
 * is untouched and keeps showing the payment and shipping state next to it.
 */
const PickingStatusTag = ({ order }) => {
  const { t } = useTranslation();
  const pickerLabel = usePickerLabel();
  if (order.containsOnlyGiftCards) return <span style={{ color: "#bfbfbf" }}>{t("order.picking_not_applicable")}</span>;
  const picking = order.picking;
  if (!picking || !picking.status) return <span style={{ color: "#bfbfbf" }}>-</span>;
  const exceptions = (picking.exceptions || []).length;
  return (
    <Tooltip
      title={[
        picking.waveNumber ? t("order.picking_round", { number: picking.waveNumber }) : null,
        picking.pickerId ? t("order.picking_by", { picker: pickerLabel(picking.pickerId) }) : null,
        picking.completedAt ? t("order.picking_done_at", { when: time(picking.completedAt) }) : null,
        exceptions > 0 ? t("order.picking_exceptions", { count: exceptions }) : null,
      ]
        .filter(Boolean)
        .join(" · ")}
    >
      <Tag color={COLORS[picking.status] || "default"}>{t(`order.picking_${picking.status}`)}</Tag>
    </Tooltip>
  );
};

/** The picking block in the order drawer: round, picker, times, boxes and what went wrong. */
export const PickingSummary = ({ picking }) => {
  const { t } = useTranslation();
  const pickerLabel = usePickerLabel();
  if (!picking || !picking.status) return null;
  const boxes = picking.boxes || [];
  const exceptions = picking.exceptions || [];
  return (
    <Descriptions title={t("order.picking_title")} column={1} size="small" className="mt-4">
      <Descriptions.Item label={t("status")}>
        <PickingStatusTag order={{ picking }} />
      </Descriptions.Item>
      <Descriptions.Item label={t("order.picking_round_label")}>
        {picking.waveNumber ? t("order.picking_round", { number: picking.waveNumber }) : "-"}
        {picking.pickDay ? ` · ${picking.pickDay}` : ""}
      </Descriptions.Item>
      <Descriptions.Item label={t("order.picking_picker")}>{pickerLabel(picking.pickerId) || "-"}</Descriptions.Item>
      <Descriptions.Item label={t("order.picking_times")}>
        {time(picking.startedAt)} – {time(picking.completedAt)}
      </Descriptions.Item>
      {boxes.length > 0 && (
        <Descriptions.Item label={t("order.picking_boxes")}>
          {boxes
            .map((box) =>
              box.actualSize && box.actualSize !== box.predictedSize
                ? t("order.picking_box_overridden", { n: box.n, predicted: box.predictedSize || "-", actual: box.actualSize })
                : t("order.picking_box", { n: box.n, size: box.actualSize || box.predictedSize || "-" }),
            )
            .join(", ")}
        </Descriptions.Item>
      )}
      {exceptions.length > 0 && (
        <Descriptions.Item label={t("order.picking_exceptions_label")}>
          {exceptions.map((exception, index) => (
            <div key={index}>
              {t(`order.picking_exception_${exception.type}`, { location: exception.location || "-", missing: exception.missing ?? "-" })}
              {exception.articleCode ? ` (${exception.articleCode})` : ""}
            </div>
          ))}
        </Descriptions.Item>
      )}
    </Descriptions>
  );
};

export default PickingStatusTag;
